import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { authApi, extractApiError } from '../lib/api';

export default function PasswordResetVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [smsMessage, setSmsMessage] = useState('');

  // SMS 발송 상태
  const [smsSent, setSmsSent] = useState(false);
  const [phoneMasked, setPhoneMasked] = useState('');
  const [smsExpiresAt, setSmsExpiresAt] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(0);

  // URL에서 토큰 추출
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  // SMS 재발송 카운트다운
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [countdown]);

  // SMS 인증 코드 요청
  const handleRequestSms = async () => {
    if (!token.trim()) {
      setError('이메일 링크의 토큰이 필요합니다.');
      return;
    }

    setSmsLoading(true);
    setError('');
    setSmsMessage('');

    try {
      const response = await authApi.requestResetSmsCode(token);

      if (response.success && response.data) {
        setSmsSent(true);
        setPhoneMasked(response.data.phoneMasked);
        setSmsExpiresAt(new Date(response.data.expiresAt));
        setSmsMessage(response.data.message);
        setCountdown(60); // 60초 후 재발송 가능
      } else {
        const errorMessage = response.error?.message || 'SMS 발송에 실패했습니다.';
        setError(errorMessage);
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setSmsLoading(false);
    }
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return '비밀번호는 8자 이상이어야 합니다.';
    }
    if (!/[a-zA-Z]/.test(password)) {
      return '비밀번호에 영문자가 포함되어야 합니다.';
    }
    if (!/\d/.test(password)) {
      return '비밀번호에 숫자가 포함되어야 합니다.';
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return '비밀번호에 특수문자가 포함되어야 합니다.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검사
    if (!token.trim()) {
      setError('이메일 링크의 토큰이 필요합니다.');
      return;
    }

    if (!smsSent) {
      setError('먼저 SMS 인증 코드를 발송해주세요.');
      return;
    }

    if (smsCode.length !== 6) {
      setError('SMS 인증 코드는 6자리입니다.');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.verifyPasswordReset(token, smsCode, newPassword);

      if (response.success && response.data) {
        setSuccess(true);
      } else {
        const errorMessage = response.error?.message || '비밀번호 재설정에 실패했습니다.';
        setError(errorMessage);
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-white p-8 rounded-card-lg shadow-xl w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">비밀번호 변경 완료</h2>
            <p className="text-gray-600 mb-6">
              새 비밀번호로 로그인해주세요.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              로그인하기
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="bg-white p-8 rounded-card-lg shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">비밀번호 재설정</h1>
          <p className="text-sm text-gray-600 mt-2">
            SMS 인증 후 새 비밀번호를 설정하세요
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          {smsMessage && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-green-600 text-sm">
              {smsMessage}
            </div>
          )}

          {/* Step 1: 토큰 확인 */}
          {!searchParams.get('token') && (
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-gray-700">
                이메일 토큰
              </label>
              <input
                type="text"
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
                placeholder="이메일 링크의 토큰을 입력하세요"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                이메일로 받은 링크를 클릭하면 자동으로 입력됩니다.
              </p>
            </div>
          )}

          {searchParams.get('token') && !smsSent && (
            <div className="p-3 bg-brand-50 border border-brand-200 rounded">
              <p className="text-sm text-brand-800">
                이메일 링크가 확인되었습니다. SMS 인증 코드를 발송해주세요.
              </p>
            </div>
          )}

          {/* Step 2: SMS 인증 코드 발송 */}
          {!smsSent ? (
            <div>
              <Button
                variant="primary"
                size="lg"
                onClick={handleRequestSms}
                disabled={smsLoading || !token.trim()}
                className="w-full"
              >
                {smsLoading ? 'SMS 발송 중...' : 'SMS 인증 코드 발송'}
              </Button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                등록된 휴대폰 번호로 인증 코드가 발송됩니다.
              </p>
            </div>
          ) : (
            <>
              {/* SMS 발송 완료 상태 */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{phoneMasked}</span>로 인증 코드가 발송되었습니다.
                </p>
                {smsExpiresAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    유효 시간: {Math.ceil((smsExpiresAt.getTime() - Date.now()) / 60000)}분
                  </p>
                )}
              </div>

              {/* 재발송 버튼 */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleRequestSms}
                  disabled={smsLoading || countdown > 0}
                  className="text-sm text-brand-600 hover:text-brand-800 disabled:text-gray-400"
                >
                  {countdown > 0 ? `재발송 (${countdown}초)` : '인증 코드 재발송'}
                </button>
              </div>

              {/* Step 3: SMS 코드 입력 */}
              <div>
                <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700">
                  SMS 인증 코드
                </label>
                <input
                  type="text"
                  id="smsCode"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="6자리 숫자"
                  maxLength={6}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 font-mono text-center text-lg tracking-widest"
                />
              </div>

              {/* Step 4: 새 비밀번호 입력 */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  새 비밀번호
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="8자 이상, 영문/숫자/특수문자 조합"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  새 비밀번호 확인
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="새 비밀번호를 다시 입력하세요"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading}
                className="w-full"
              >
                {loading ? '처리 중...' : '비밀번호 변경'}
              </Button>
            </>
          )}

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              로그인으로 돌아가기
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
